import React from 'react';
import { Link } from 'react-router-dom';

import { Badge, Callout, Step } from './shared';

const CodingAgent: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Coding Agent</h2>
    <p className="text-base-content/50 text-sm mb-5">Describe what you want built — MFlow writes the code.</p>
    <p className="text-sm text-base-content/70 mb-5 leading-relaxed">
      The Coding Agent scaffolds a fresh project or works against an existing GitHub repo inside its own isolated
      sandbox, working through your request autonomously while you watch its progress in real time.
    </p>

    <div className="space-y-3 mb-6">
      <Step n={1}>
        Go to <strong>Coding agent</strong> in the sidebar and describe what you want built — the more specific, the
        better the result.
      </Step>
      <Step n={2}>
        Optionally connect an existing GitHub repo instead of starting from scratch: pick one from your connected
        account, or type <code className="bg-base-200 px-1 rounded text-xs">owner/repo</code> directly, then choose a
        branch (defaults to that repo&apos;s own default branch).
      </Step>
      <Step n={3}>Pick a sandbox size — see below — then click Start task.</Step>
      <Step n={4}>
        Watch the live execution log as the agent works. You can stop a task at any point, whether it&apos;s still
        queued or already running, and any work done up to that point is kept.
      </Step>
    </div>

    <h3 className="font-semibold mb-2">Sandbox size</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      Every task runs in an isolated sandbox sized for the work. Pick the tier that matches what you&apos;re asking for
      — a bigger sandbox handles heavier builds without running out of memory, but doesn&apos;t make simple tasks finish
      any faster.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
      {[
        {
          label: 'Light',
          color: 'badge-ghost',
          spec: '2GB RAM · 1 CPU',
          desc: 'Landing pages, simple scripts, documentation tweaks.',
        },
        {
          label: 'Mid',
          color: 'badge-info',
          spec: '3GB RAM · 1 CPU (2 cores)',
          desc: 'Default — a good fit for most typical projects.',
        },
        {
          label: 'Heavy',
          color: 'badge-warning',
          spec: '7GB RAM · 3 CPU (4 cores)',
          desc: 'Large monorepos, intensive frontend builds (CRA, Webpack, Vite, Next.js).',
        },
      ].map((t) => (
        <div key={t.label} className="rounded-xl bg-base-200 shadow-card p-4 space-y-1.5">
          <Badge color={t.color}>{t.label}</Badge>
          <div className="text-xs text-base-content/50">{t.spec}</div>
          <div className="text-xs text-base-content/60 leading-relaxed">{t.desc}</div>
        </div>
      ))}
    </div>

    <Callout type="tip">
      Picking a bigger tier than a task needs is harmless, just a little wasteful. Picking one that&apos;s too small for
      a heavy build (a large monorepo install, a Webpack/Vite production build) is the more common mistake — it can run
      the sandbox out of memory partway through.
    </Callout>

    <p className="text-sm text-base-content/70 mt-4 mb-4 leading-relaxed">
      Each size has its own monthly limit on how many sandboxes you can start, shown on its card as{' '}
      <code className="bg-base-200 px-1 rounded text-xs">used/limit starts this month</code>. This limit applies even
      when you use your own API key, because the sandbox runs on MFlow&apos;s servers whichever model drives it. When a
      size&apos;s limit runs out, start the task on a different size or wait for next month.
    </p>

    <h3 className="font-semibold mb-2 mt-6">Waiting in the queue</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      When all sandbox servers are busy, or you already have as many tasks running as your plan allows, a new task
      doesn&apos;t fail. It waits with a <strong>Queued</strong> status and a <strong>Position N</strong> badge, where 1
      means next in line, and starts on its own when a slot opens. The position is approximate: a smaller task can
      sometimes start ahead of a bigger one if it fits in the space that&apos;s free. Click <strong>Stop</strong> to
      cancel a queued task before it starts. If a task waits too long, it ends as <em>failed</em> with an error saying
      why.
    </p>

    <h3 className="font-semibold mb-2 mt-6">AI provider &amp; cost</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      By default tasks run on MFlow&apos;s hosted key, which counts against a monthly task limit shown at the top of the
      page. Add your own API key for Gemini, OpenAI, Claude, or DeepSeek from <strong>Integrations</strong> to run on
      that provider instead — bring-your-own-key tasks skip the monthly task limit (the per-size sandbox limit above
      still applies). See{' '}
      <Link to="/docs/ai-providers" className="link link-primary">
        AI Providers &amp; Keys
      </Link>
      . As you type your prompt, MFlow shows a rough pre-task cost estimate per provider so you can compare before
      starting; the real cost (and token usage) appears on the task once it finishes.
    </p>

    <h3 className="font-semibold mb-2">Linking a task to a Standalone task</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      Starting a coding-agent run from <strong>Assign to agent</strong> on a Standalone task links the two together —
      the agent gets extra read-only context about that task and project, and posts a summary or error comment back onto
      the task automatically once it finishes.
    </p>

    <h3 className="font-semibold mb-2">Moving the task on the board automatically</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      A linked Standalone task can move across the board as the agent works. In the project&apos;s{' '}
      <strong>Board settings → Columns</strong>, give an <em>In progress</em>-type column a coding-agent role:
    </p>
    <ul className="text-sm text-base-content/70 space-y-1 list-disc list-inside mb-3">
      <li>
        <Badge color="badge-primary">Working</Badge> — the task moves here when the agent starts running.
      </li>
      <li>
        <Badge color="badge-primary">Review</Badge> — the task moves here when the agent finishes. If the run fails or
        is cancelled, the task goes back to the column it was in before.
      </li>
    </ul>
    <Callout type="info">
      No roles are set by default. If the project is missing either role, MFlow warns you before the task starts that
      the board won&apos;t update. From there you can open settings to fix it, or continue anyway.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">Pull requests &amp; review feedback</h3>
    <p className="text-sm text-base-content/70 mb-3 leading-relaxed">
      When a task on an existing repo finishes and opens a pull request, the task page shows a PR card with a link to
      the PR, its review status (<Badge color="badge-warning">Changes requested</Badge>{' '}
      <Badge color="badge-success">Approved</Badge> <Badge color="badge-info">Commented</Badge>{' '}
      <Badge color="badge-success">Merged</Badge> <Badge>Closed</Badge>), and the number of unresolved review comments.
      MFlow checks the PR every 15 minutes. Click <strong>Check now</strong> to refresh it straight away.
    </p>
    <div className="space-y-3 mb-4">
      <Step n={1}>
        When a reviewer requests changes or leaves comments, a <strong>Resume — address feedback</strong> button appears
        on the PR card. You can add extra context for the agent first if you like.
      </Step>
      <Step n={2}>
        Resuming starts a <strong>new</strong> task, labelled <em>Resumed from a previous task</em>. It reads the review
        comments, remembers what the earlier run did, changes the code, and pushes commits to the{' '}
        <strong>same branch and PR</strong>. It never opens a second PR.
      </Step>
      <Step n={3}>
        You can repeat this for each new round of review. The button goes away once the PR is merged or closed.
      </Step>
    </div>
    <Callout type="warning">
      Resuming is a manual action, on purpose. The agent won&apos;t push new commits to an open PR by itself whenever
      someone comments, so reviewers aren&apos;t surprised in the middle of a review. A resume counts toward your usage
      limits like any other task.
    </Callout>

    <h3 className="font-semibold mb-2 mt-6">Architecture decisions</h3>
    <p className="text-sm text-base-content/70 mb-4 leading-relaxed">
      When the agent works on a repo, it also writes the project&apos;s ADRs for that repo into its{' '}
      <code className="bg-base-200 px-1 rounded text-xs">docs/adr/</code> folder. If you&apos;ve turned on the weekly
      drift check, you&apos;ll also see automated tasks labelled <em>🔍 Automated: architecture decision check</em>.
      These are free and don&apos;t count toward your limits. See{' '}
      <Link to="/docs/knowledge-base" className="link link-primary">
        Knowledge Base &amp; ADRs
      </Link>
      .
    </p>

    <Callout type="info">
      This is a different agent from the platform assistants covered in{' '}
      <Link to="/docs/ai-chat" className="link link-primary">
        AI Assistant
      </Link>{' '}
      — it writes and runs code inside its own sandbox rather than reading or editing your Jira, Trello, or Standalone
      data.
    </Callout>
  </div>
);

export default CodingAgent;
